import { FormRadioGroup as GelRadioGroup } from '@snsw/react-component-library';
import { ForwardedRef, forwardRef, ReactNode, useMemo } from 'react';
import { useLatest } from 'react-use';
import { KiamaRadioGroupOption } from '../../../types/kiama';
import { LookupValue, PropsWithForwardedRef } from '../../../types/types';
import { extractKey } from '../ObjectWithId';

export interface RadioGroupOption<TValue> extends LookupValue<TValue> {
  helpMessage?: ReactNode;
}

export interface RadioGroupProps<TValue> {
  id?: string;
  name: string;
  label?: ReactNode;
  helpMessage?: ReactNode;
  errorMessage?: string;
  options: LookupValue<TValue>[] | RadioGroupOption<TValue>[];
  getOptionKey?: (value: TValue) => string;
  vertical?: boolean;
  margin?: { top: number | 'none' | 'xs' | 'sm' | 'md' | 'lg' | 'xl' | 'xxl' | 'xxxl' };
  className?: string;
  value?: TValue;
  onChange?: (next: TValue) => void;
}

const useRadioOptions = <TValue extends unknown = string>(
  options: RadioGroupProps<TValue>['options'],
  getOptionKey?: RadioGroupProps<TValue>['getOptionKey']
): [KiamaRadioGroupOption<string | boolean>[], 'boolean' | undefined] => {
  const getOptionKeyRef = useLatest(getOptionKey);

  return useMemo(() => {
    const getOptionValue = getOptionKeyRef.current ?? extractKey;
    let isBoolean = true;

    const result: KiamaRadioGroupOption<string | boolean>[] = options.map(({ label, value, helpMessage }) => {
      isBoolean = isBoolean && typeof value === 'boolean';

      return {
        label,
        value: getOptionValue(value),
        clarify: helpMessage,
      };
    });

    return [result, isBoolean ? 'boolean' : undefined];
  }, [options]);
};

const RadioGroupWithRef = <TValue extends unknown>(
  props: RadioGroupProps<TValue>,
  ref: ForwardedRef<HTMLInputElement>
) => {
  const { id, name, label, options, value, onChange, errorMessage, getOptionKey, ...radioGroupProps } = props;

  const handleChange = (next: string | boolean) => {
    if (!onChange) return;

    const getOptionValue = getOptionKey ?? extractKey;

    const selectedOption = options.find((o) => getOptionValue(o.value) === next);
    onChange && onChange(selectedOption?.value);
  };

  const [selectOptions, fieldType] = useRadioOptions(options);
  const selectedValue = extractKey(value);

  return (
    <GelRadioGroup
      {...radioGroupProps}
      id={id}
      name={name}
      legend={label}
      inputRef={ref}
      value={selectedValue}
      options={selectOptions}
      onChange={handleChange}
      fieldType={fieldType}
      hasError={!!errorMessage}
      errorMessage={errorMessage}
    />
  );
};

export const RadioGroup = forwardRef(RadioGroupWithRef) as <TValue extends unknown>(
  props: PropsWithForwardedRef<RadioGroupProps<TValue>, HTMLInputElement>
) => ReturnType<typeof RadioGroupWithRef>;
